// Which terminal statement a branch shows where its distribution used to be — or `null`
// while it is still a live forecast.
//
// This lived as a nested ternary at the render site, and that is where it went wrong: a
// premise the grader had returned INCONCLUSIVE on fell through to "superseded", whose panel
// says "The main forecast has resolved" — about a trunk that had not. Five states with two
// independent inputs is past the size a conditional can be read correctly in place, so the
// choice is made here, once, against a table.
//
// The ordering rule, which is the whole content of this function: what happened to the
// BRANCH'S OWN premise outranks what happened to the trunk. `superseded` is therefore
// reserved for a premise that is still PENDING when the root resolves — the one case where
// the trunk is genuinely the only thing that has happened, which is exactly what its copy
// claims.

/**
 * The terminal state for a branch, or `null` if it is still live.
 *
 * @param {object} args
 * @param {string|null|undefined} args.premiseState  pending | confirmed | contradicted | indeterminate
 * @param {string|null|undefined} args.verdictStatus the resolver verdict behind that state, if any
 * @param {boolean} args.rootHasTerminalVerdict      has the ROOT question resolved?
 * @returns {"confirmed"|"contradicted"|"expired"|"undecided"|"superseded"|null}
 */
export function branchTerminalState(args) {
  const premiseState = args?.premiseState ?? null;
  if (premiseState === "confirmed") return "confirmed";
  if (premiseState === "contradicted") return "contradicted";
  if (premiseState === "indeterminate") {
    // Both mean "no answer", for opposite reasons, and collapsing them tells the user
    // something false about what was done. A verdict means someone asked and the world was
    // ambiguous; no verdict means nobody ever asked, because nothing was ever mintable.
    return args?.verdictStatus ? "undecided" : "expired";
  }
  // Pending (or senseless) under a resolved root: the trunk is the only thing that has
  // happened, which is what `superseded` says.
  return args?.rootHasTerminalVerdict ? "superseded" : null;
}

/**
 * Does this terminal state grey the branch's scaffolding?
 *
 * Everything except `confirmed`: a confirmed branch's assumption held, so dimming it would
 * deny a path that actually happened. `null` (still live) is obviously not dead.
 */
export function branchScaffoldDead(terminalState) {
  return terminalState !== null && terminalState !== undefined && terminalState !== "confirmed";
}
